import React from 'react';
import { Alert, Spinner } from 'react-bootstrap';
import { Form, FormGroup } from '~/components/Form';
import { ClientContext, ServicesContext, useAdminService } from '~/services';
import { enums } from '~/util';
import { Select } from '~/components/Select';

interface BasicInfoFormProps {
    id: string;
    onCancel: () => void;
    user: UserType;
}
export function BasicInfoForm({ id, onCancel, user }: BasicInfoFormProps) {
    const servicesContext = React.useContext(ServicesContext);
    const client = React.useContext(ClientContext);
    const { updateUser } = useAdminService();
    const [firstName, setFirstName] = React.useState(user.first_name);
    const [lastName, setLastName] = React.useState(user.last_name);
    const [title, setTitle] = React.useState(user.title);
    const [accessType, setAccessType] = React.useState({
        value: user.access_type,
        label: enums(['user', 'access_type', user.access_type]),
    });
    const [responseMessage, setResponseMessage] = React.useState<null | { message: string }[]>(
        null
    );
    const [submitting, setSubmitting] = React.useState(false);

    /**
     * @param {array<string>} touchedFields
     * @returns {Promise<void>}
     */
    async function handleSubmit(touchedFields: string[]) {
        if (touchedFields.length > 0 || user.access_type !== accessType.value) {
            // email is purposefully not updatable
            const userUpdated: {
                first_name?: string; // eslint-disable-line babel/camelcase
                last_name?: string; // eslint-disable-line babel/camelcase
                title?: string;
                access_type?: number; // eslint-disable-line babel/camelcase
            } = {};

            [
                { id: 'first_name', val: firstName },
                { id: 'last_name', val: lastName },
                { id: 'title', val: title },
            ].forEach((obj: { id: 'first_name' | 'last_name' | 'title'; val: string }) => {
                if (touchedFields.includes(obj.id)) {
                    userUpdated[obj.id] = obj.val;
                }
            });
            if (user.access_type !== accessType.value) {
                userUpdated.access_type = Number(accessType.value);
            }

            try {
                // Update user
                setResponseMessage(null);
                setSubmitting(true);
                await updateUser(user.id, userUpdated, {
                    handledErrors: { 400: true },
                });
            } catch (e) {
                // Handle failed submissions
                setResponseMessage(e.response);
                setSubmitting(false);
            }
        } else {
            // Cancel submission, no data has changed
            onCancel();
        }
    }

    return (
        <Form
            id={id}
            disabled={submitting}
            onSubmit={handleSubmit}
            values={{
                first_name: firstName,
                last_name: lastName,
                title,
                access_type: accessType,
            }}
        >
            <div className="row">
                <FormGroup htmlFor="first_name" className="col-md-6">
                    <label htmlFor="first_name" className="col-form-label">
                        First Name
                    </label>
                    <input
                        type="text"
                        id="first_name"
                        className="form-control"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        required
                    />
                </FormGroup>
                <FormGroup htmlFor="last_name" className="col-md-6">
                    <label htmlFor="last_name" className="col-form-label">
                        Last Name
                    </label>
                    <input
                        type="text"
                        id="last_name"
                        className="form-control"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        required
                    />
                </FormGroup>
            </div>
            <div className="row">
                <FormGroup htmlFor="title" className="col-md-6">
                    <label htmlFor="title" className="col-form-label">
                        Title
                    </label>
                    <input
                        type="text"
                        id="title"
                        className="form-control"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                    />
                </FormGroup>
            </div>
            <label htmlFor="email" className="col-form-label">
                Email
            </label>
            <input type="text" id="email" className="form-control" value={user.email} disabled />
            <FormGroup htmlFor="access_type">
                <label htmlFor="access_type" className="col-form-label">
                    Access Type
                </label>
                <Select
                    id="access_type"
                    options={client.availableUserAccessTypes(user.email)}
                    value={accessType}
                    placeholder="Access Type"
                    label="Access Type"
                    menuPosition="fixed"
                    onChange={setAccessType}
                    isDisabled={!servicesContext.user.isGlobalAdmin()}
                />
            </FormGroup>
            {responseMessage && (
                <Alert variant="danger" className="mt-sm-3">
                    {Array.isArray(responseMessage) ? (
                        <ul>
                            {responseMessage.map((x) => (
                                <li key={x.message}>{x.message}</li>
                            ))}
                        </ul>
                    ) : (
                        responseMessage
                    )}
                </Alert>
            )}
            <div className="text-end mt-4">
                <button type="button" className="btn btn-secondary" onClick={onCancel}>
                    Cancel
                </button>
                <button type="submit" className="btn btn-primary ms-2" disabled={submitting}>
                    Save
                    {submitting && (
                        <Spinner
                            animation="border"
                            variant="light"
                            size="sm"
                            role="status"
                            className="ms-1"
                        />
                    )}
                </button>
            </div>
        </Form>
    );
}
